import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CheckBox,
  FormField,
  Heading,
  Select,
  Spinner,
  Text,
  TextInput,
} from "grommet";
import { Add, Checkmark, Trash, StatusWarning } from "grommet-icons";
import moment from "moment-timezone";
import { presentAlertPrompt } from "@shared/Alert";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { BookingCalendarInput, HourInput } from "@shared";
import passcodeService from "@services/passcode.service";
import { useFetchDoors, useFetchHotel } from "./redux/hooks";
import { useTranslation } from "react-i18next";
import { supportsOfflineFallback } from "../../common/offerCapabilities";

export default function DoorPasscodes() {
  const { t } = useTranslation();
  const { doors } = useFetchDoors();
  const { hotel } = useFetchHotel();
  const canUseOfflineFallback = supportsOfflineFallback(hotel);

  const eligibleDoors = (doors || []).filter(
    (d) => !!d.lockId && !d.validNoLock,
  );

  const [selectedDoor, setSelectedDoor] = useState(null);
  const [passcodes, setPasscodes] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");
  const [formStartDate, setFormStartDate] = useState(null); // moment object
  const [formEndDate, setFormEndDate] = useState(null); // moment object
  const [formStartTime, setFormStartTime] = useState(""); // "HH:mm"
  const [formEndTime, setFormEndTime] = useState(""); // "HH:mm"
  const [formOffline, setFormOffline] = useState(false);
  const useOffline = canUseOfflineFallback && formOffline;
  const [submitting, setSubmitting] = useState(false);

  const tz = hotel?.timezone || "UTC";

  const loadPasscodes = useCallback(async (door) => {
    if (!door) return;
    setLoadingList(true);
    try {
      const res = await passcodeService.getDoorPasscodes(door._id);
      setPasscodes(res.data || []);
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadPasscodes(selectedDoor);
  }, [selectedDoor, loadPasscodes]);

  const openForm = () => {
    const now = moment().tz(tz);
    const tomorrow = moment().tz(tz).add(1, "days");
    // Floor to the nearest past 30-minute mark (e.g. 10:46 → "10:30")
    const flooredMinutes = Math.floor(now.minutes() / 30) * 30;
    const startTime = now
      .clone()
      .minutes(flooredMinutes)
      .seconds(0)
      .format("HH:mm");
    const checkoutTime = hotel?.checkoutTime || tomorrow.format("HH:mm");
    setFormStartDate(now);
    setFormEndDate(tomorrow);
    setFormStartTime(startTime);
    setFormEndTime(checkoutTime);
    setFormCode("");
    setFormName("");
    setFormOffline(false);
    setShowForm(true);
  };

  // BookingCalendarInput gives back (startDateStr, endDateStr) — preserve the times
  const handleDatesChange = (startDateStr, endDateStr) => {
    const startDay = moment(startDateStr).format("YYYY-MM-DD");
    const endDay = moment(endDateStr).format("YYYY-MM-DD");
    setFormStartDate(moment.tz(`${startDay} ${formStartTime}`, tz));
    setFormEndDate(moment.tz(`${endDay} ${formEndTime}`, tz));
  };

  const handleStartTimeChange = ({ value }) => {
    setFormStartTime(value);
    if (formStartDate) {
      const day = moment(formStartDate).format("YYYY-MM-DD");
      setFormStartDate(moment.tz(`${day} ${value}`, tz));
    }
  };

  const handleEndTimeChange = ({ value }) => {
    setFormEndTime(value);
    if (formEndDate) {
      const day = moment(formEndDate).format("YYYY-MM-DD");
      setFormEndDate(moment.tz(`${day} ${value}`, tz));
    }
  };

  const handleCreate = async () => {
    if (!selectedDoor || !formName || !formStartDate || !formEndDate) return;
    if (!useOffline && !formCode) return;

    setSubmitting(true);
    try {
      await passcodeService.createDoorPasscode(selectedDoor._id, {
        code: useOffline ? undefined : formCode,
        name: formName,
        startDate: formStartDate.toISOString(),
        endDate: formEndDate.toISOString(),
        offline: useOffline,
      });
      presentToastSuccess(t("passcodes.doorCodeCreated"));
      setShowForm(false);
      await loadPasscodes(selectedDoor);
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = useCallback(
    (passcode) => {
      presentAlertPrompt({
        title: t("passcodes.deleteConfirmTitle"),
        message: t("passcodes.deleteDoorCodeMessage", {
          name: passcode.keyboardPwdName,
        }),
        buttonOKText: t("common.delete"),
        buttonOKColor: "status-critical",
        onOK: async () => {
          presentLoading();
          try {
            await passcodeService.deleteDoorPasscode(
              selectedDoor._id,
              passcode._id,
            );
            presentToastSuccess(t("passcodes.deleted"));
            await loadPasscodes(selectedDoor);
          } catch (err) {
            presentToastErrorContent(err);
          } finally {
            dismissLoading();
          }
        },
      });
    },
    [selectedDoor, loadPasscodes, t],
  );

  const formatDate = (date) => {
    if (!date) return "—";
    return moment(date).tz(tz).format("MMM D, YYYY HH:mm");
  };

  const isFormValid =
    !!formName &&
    !!formStartDate &&
    !!formEndDate &&
    (useOffline || !!formCode) &&
    !submitting;

  return (
    <Box gap="medium" width={{ max: "680px" }}>
      {/* Header */}
      <Heading level="3" margin="none">
        {t("passcodes.doorCodesTitle")}
      </Heading>

      {/* Emergency-use notice */}
      <Box
        direction="row"
        gap="small"
        align="start"
        pad="small"
        background="light-2"
        border={{ color: "status-warning", size: "small" }}
        round="xsmall"
      >
        <StatusWarning
          color="status-warning"
          size="small"
          style={{ flexShrink: 0, marginTop: 2 }}
        />
        <Text size="small" color="dark-2">
          {t("passcodes.doorCodesEmergencyNotice")}
        </Text>
      </Box>

      {/* Door selector */}
      <FormField label={t("passcodes.selectDoor")}>
        <Select
          options={eligibleDoors}
          labelKey="name"
          valueKey={{ key: "_id", reduce: false }}
          value={selectedDoor}
          onChange={({ value }) => {
            setSelectedDoor(value);
            setShowForm(false);
          }}
          placeholder={t("passcodes.selectDoorPlaceholder")}
        />
      </FormField>

      {selectedDoor && (
        <Box gap="medium">
          {/* Create form toggle */}
          {!showForm ? (
            <Button
              icon={<Add size="small" />}
              label={t("passcodes.addDoorCode")}
              primary
              onClick={openForm}
              alignSelf="start"
              size="small"
            />
          ) : (
            <Box
              border
              pad="medium"
              round="xsmall"
              gap="small"
              background="light-1"
            >
              <Heading level="4" margin={{ bottom: "small", top: "none" }}>
                {t("passcodes.newDoorCode")}
              </Heading>

              <FormField label={t("passcodes.name")} required>
                <TextInput
                  placeholder={t("passcodes.namePlaceholder")}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </FormField>

              <FormField label={t("passcodes.dates")}>
                <BookingCalendarInput
                  checkinDate={formStartDate}
                  checkoutDate={formEndDate}
                  onChange={handleDatesChange}
                  bounds={[
                    moment().tz(tz).startOf("day").toISOString(),
                    moment().tz(tz).add(5, "years").toISOString(),
                  ]}
                />
              </FormField>

              <Box direction="row" gap="small">
                <FormField label={t("passcodes.startTime")} style={{ flex: 1 }}>
                  <HourInput
                    name="startTime"
                    value={formStartTime}
                    onChange={({ value }) => handleStartTimeChange({ value })}
                    timezone={tz}
                  />
                </FormField>
                <FormField label={t("passcodes.endTime")} style={{ flex: 1 }}>
                  <HourInput
                    name="endTime"
                    value={formEndTime}
                    onChange={({ value }) => handleEndTimeChange({ value })}
                    timezone={tz}
                  />
                </FormField>
              </Box>

              {canUseOfflineFallback && (
                <CheckBox
                  checked={useOffline}
                  label={t("passcodes.offlineCode")}
                  onChange={(e) => setFormOffline(e.target.checked)}
                />
              )}
              {useOffline && (
                <Text size="small" color="dark-4">
                  {t("passcodes.offlineCodeHint")}
                </Text>
              )}

              {!useOffline && (
                <FormField label={t("passcodes.code")} required>
                  <TextInput
                    placeholder={t("passcodes.codePlaceholder")}
                    value={formCode}
                    onChange={(e) => setFormCode(e.target.value)}
                    type="text"
                    inputMode="numeric"
                  />
                </FormField>
              )}

              <Box direction="row" gap="small" justify="end">
                <Button
                  label={t("common.cancel")}
                  onClick={() => setShowForm(false)}
                />
                <Button
                  label={
                    submitting ? (
                      <Spinner size="xsmall" />
                    ) : (
                      t("passcodes.create")
                    )
                  }
                  primary
                  icon={submitting ? undefined : <Checkmark size="small" />}
                  onClick={handleCreate}
                  disabled={!isFormValid}
                />
              </Box>
            </Box>
          )}

          {/* List */}
          {loadingList ? (
            <Box align="center" pad="medium">
              <Spinner />
            </Box>
          ) : passcodes.length === 0 ? (
            <Text color="dark-4">{t("passcodes.noDoorCodes")}</Text>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}
                >
                  <th style={{ padding: "8px" }}>{t("passcodes.colName")}</th>
                  <th style={{ padding: "8px" }}>{t("passcodes.colCode")}</th>
                  <th style={{ padding: "8px" }}>{t("passcodes.colStart")}</th>
                  <th style={{ padding: "8px" }}>{t("passcodes.colEnd")}</th>
                  <th style={{ padding: "8px" }} />
                </tr>
              </thead>
              <tbody>
                {passcodes.map((p) => {
                  const isExpired =
                    p.endDate && new Date(p.endDate) < new Date();
                  return (
                    <tr
                      key={p._id}
                      style={{
                        borderBottom: "1px solid #eee",
                        opacity: isExpired ? 0.5 : 1,
                      }}
                    >
                      <td style={{ padding: "8px" }}>
                        {p.keyboardPwdName}
                        {p.offline && (
                          <Text size="xsmall" color="dark-4">
                            {" "}
                            ({t("passcodes.offline")})
                          </Text>
                        )}
                        {isExpired && (
                          <Text size="xsmall" color="status-critical">
                            {" "}
                            ({t("accessList.expired")})
                          </Text>
                        )}
                      </td>
                      <td style={{ padding: "8px", fontFamily: "monospace" }}>
                        {p.keyboardPwd || "—"}
                      </td>
                      <td style={{ padding: "8px" }}>
                        {formatDate(p.startDate)}
                      </td>
                      <td style={{ padding: "8px" }}>
                        {formatDate(p.endDate)}
                      </td>
                      <td style={{ padding: "8px" }}>
                        <Button
                          icon={<Trash size="small" color="status-critical" />}
                          onClick={() => handleDelete(p)}
                          plain
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Box>
      )}
    </Box>
  );
}
